<template>
    <div class="password-reset content-wrapper">
        <div class="title-wrapper">
            <span class="bracket-wrapper">
                <img class="bracket" src="~@/assets/celtic_bracket.png" />
            </span>
            <h1 class="title">
                Password Reset
            </h1>
            <span class="bracket-wrapper left">
                <img class="bracket flipped" src="~@/assets/celtic_bracket.png" />
            </span>
        </div>
        <div class="content">
            <div class="instructions" v-if="form.token">
                Enter your new password.
            </div>
            <div class="instructions" v-if="!form.token">
                Enter the email address associated with your account,
                and we’ll email you a link to reset your password.
            </div>
            <form class="login" v-on:submit.prevent="onSubmit">
                <div class="form-control-wrapper" v-if="form.token">
                    <input
                        class="form-control"
                        v-model="form.password"
                        placeholder="Password"
                        name="password"
                        type="password"
                        autocomplete="off"
                        @input="onInputChange"
                    />
                    <div class="form-error" v-if="errors.password">
                        {{ errors.password }}
                    </div>
                </div>
                <div class="form-control-wrapper" v-if="!form.token">
                    <input
                        class="form-control"
                        v-model="form.email"
                        placeholder="Email"
                        name="email"
                        autocomplete="off"
                        @input="onInputChange"
                    />
                    <div class="form-error" v-if="errors.email">
                        {{ errors.email }}
                    </div>
                </div>
                <div class="form-control-wrapper">
                    <input class="btn btn-primary btn-block" type="submit"
                        :value="form.token ? 'Set new password' : 'Send reset link'" v-if="!submitting" />
                    <button class="btn btn-primary btn-block btn-disabled" v-if="submitting">
                        <span class="btn-text">
                            Submitting<span class="ellipsis animated-ellipsis"></span>
                        </span>
                    </button>
                </div>
                <div class="back-to-login" v-if="!form.token">
                    <router-link :to="{ name: 'login' }" class="link">
                        Back to login
                    </router-link>
                </div>
            </form>
        </div>
    </div>
</template>

<script lang="ts">
import api from '../../helpers/api';

export default {
    name: 'PasswordReset',
    created() {
        if (this.defaultToken) {
            this.form.token = this.defaultToken;
        }
    },
    computed: {
        defaultToken() {
            return this.$route.query.token ? String(this.$route.query.token) : '';
        },
    },
    watch: {
        '$route.query.token': function setToken() {
            if (this.defaultToken) {
                this.form.token = this.defaultToken;
            }
        },
    },
    data() {
        return {
            form: {
                email: '',
                password: '',
                token: '',
            },
            errors: {
                email: null,
                password: null,
            },
            submitting: false,
        };
    },
    methods: {
        validateEmail(email) {
            return /[A-Z0-9._%+-]+@[A-Z0-9.-]+.[A-Z]{2,4}/i.test(email);
        },
        validatePassword(text, requiresStrongPassword) {
            const password = String(text).trim();
            return Boolean((!requiresStrongPassword && password.length) || (requiresStrongPassword && password.length >= 8));
        },
        validateForm(form) {
            const { email, password, token } = form;
            const { errors } = this;
            const requiresStrongPassword = Boolean(token);

            const passwordMsg = requiresStrongPassword ? 'Please enter a password at least 8 characters long.' :
                'Please enter a password.';
            const emailMsg = 'Please enter a valid email.';

            if (token) {
                errors.password = !this.validatePassword(password, requiresStrongPassword) ? passwordMsg : null;
            } else {
                errors.email = !this.validateEmail(email) ? emailMsg : null;
            }

            return !errors.email && !errors.password;
        },
        onInputChange(event) {
            const { name, value } = event.target;
            const capitalizedName = name.slice(0, 1).toUpperCase() + name.slice(1);
            const requiresStrongPassword = Boolean(this.form.token);

            if (this.errors[name]) {
                if (this[`validate${capitalizedName}`](value, requiresStrongPassword)) {
                    this.errors[name] = null;
                }
            }
        },
        async onSubmit() {
            const { form } = this;
            let serverMsg;

            if (!this.validateForm(form) || this.submitting) {
                return;
            }

            this.submitting = true;

            const url = `/password/${form.token ? 'reset' : 'request'}`;

            const response = await api.request(url, 'POST', form, false);

            this.submitting = false;

            if (response.ok) {
                const msg = form.token ? 'You have successfully reset your password!' :
                    `A link to reset your password has been sent to ${form.email}`;

                if (form.token) {
                    this.$store.dispatch('setAuthDetails', await response.json());
                    this.$router.push('/rooms');
                } else {
                    this.form.email = '';
                }

                this.$notify({
                    group: 'app',
                    title: 'Success',
                    type: 'success',
                    text: msg,
                });

                return;
            }

            const error = await response.json();

            switch (response.status) {
                case 400:
                case 401:
                case 404:
                case 500:
                    serverMsg = error.message;
                    break;
                default:
                    serverMsg = 'An unknown server error has occured';
            }

            this.$notify({
                group: 'app',
                title: 'Error',
                type: 'error',
                text: serverMsg,
            });
        },
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .password-reset {

        &.content-wrapper {
            text-align: center;

            .instructions {
                text-align: left;
            }

            .content {
                border-radius: 4px;
                margin: 25px auto;
                max-width: 375px;
                padding: 25px;
                position: relative;
            }

            .back-to-login {
                margin-top: -7.5px;

                .link {
                    color: $dark-brown;
                    font-size: .85em;
                }
            }

            .switch-form-toggle {
                margin: 20px auto 0 auto;
            }

            .btn-text {
                vertical-align: middle;
            }

            .spinner-wrapper {
                display: inline-block;
                vertical-align: middle;
                margin-right: 3px;
            }
        }
    }

</style>
